import css from './LogViewer.module.css';
import { useState, useEffect } from 'react';

import {
  AdminDataTable,
  DataTableRow,
  DateDataColumn,
  SortColumn,
  SortDirection,
  TextDataColumn,
  FilterColumn,
  OptionsDataFilter,
  DateRangeFilter,
  DataColumn,
  TableStatus
} from '../AdminDataTable/AdminDataTable';
import { MongoLog } from '../../../types/appModels';
import { useTableData } from '../../../hooks/useTableData';

interface LogViewerRow extends DataTableRow {
  requestId: string;
  dateTime: Date;
  logLevel: string;
  user?: string;
  className: string;
  operation: string;
  eventId: string;
  message: string;
}

const LogViewer = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Create a function to build default date filters for the last 24 hours
  const buildDefaultFilters = () => {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    return [
      {
        columnId: 'dateTimeFrom',
        filter: oneDayAgo.toISOString(),
      }
    ];
  };

  const handleError = (err: any) => {
    console.error('Error loading logs:', err);
    setError(err.message || 'Failed to load logs. Please try again later.');
  };

  const { dataState, runSort, runFilters, loadPage } = useTableData<MongoLog>({
    baseEndpoint: process.env.REACT_APP_API_LOG!,
    initialData: { count: 0, list: [] },
    defaultSort: { columnId: 'dateTime', direction: SortDirection.Descending },
    defaultSkip: 0,
    defaultLimit: 100,
    buildDefaultFilters,
    onError: handleError
  });

  const getLogLevelClass = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'trace':
        return css.logLevelTrace;
      case 'debug':
        return css.logLevelDebug;
      case 'information':
        return css.logLevelInfo;
      case 'warning':
        return css.logLevelWarning;
      case 'error':
        return css.logLevelError;
      case 'critical':
        return css.logLevelCritical;
      default:
        return css.logLevelInfo;
    }
  };

  const columns = [
    !isMobile ? new TextDataColumn('requestId', 'Request ID', true, true, 10,
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ) : null,
    new DateDataColumn(
      'dateTime',
      isMobile ? 'Time' : 'Date/Time',
      true,
      true,
      15,
      (date) => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
          return <div className={css.dateCell}>Invalid date</div>;
        }
        return (
          <div className={css.dateCell}>
            <div>{date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })}</div>
            <div>{date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(',', '')}</div>
          </div>
        );
      },
      undefined,
      undefined,
      undefined,
      (column, handleFilter) => {
        return (
          <DateRangeFilter
            column={column}
            handleFilter={handleFilter}
            defaultFromFilter={Date.now() - 24 * 60 * 60 * 1000}
          />
        );
      }
    ),
    new TextDataColumn(
      'logLevel',
      'Level',
      true,
      true,
      10,
      (value) => (
        <span className={`${css.logLevelBadge} ${getLogLevelClass(value)}`}>
          {value}
        </span>
      ),
      undefined,
      undefined,
      (column, handleFilter) => {
        return (
          <OptionsDataFilter
            column={column}
            handleFilter={handleFilter}
            options={[
              { label: 'Trace', value: 'Trace' },
              { label: 'Debug', value: 'Debug' },
              { label: 'Information', value: 'Information' },
              { label: 'Warning', value: 'Warning' },
              { label: 'Error', value: 'Error' },
              { label: 'Critical', value: 'Critical' },
            ]}
          />
        );
      }
    ),
    !isMobile ? new TextDataColumn('user', 'User', true, true, 15,
      (value) => (
        <div className={css.textCell}>{value || '-'}</div>
      )
    ) : null,
    !isMobile ? new TextDataColumn('className', 'Class', true, true, 15,
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ) : null,
    !isMobile ? new TextDataColumn('operation', 'Operation', true, true, 15,
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ) : null,
    !isMobile ? new TextDataColumn(
      'eventId',
      'Event',
      true,
      true,
      10,
      (value) => (
        <div className={css.textCell}>{value}</div>
      ),
      undefined,
      undefined,
      (column, handleFilter) => {
        return (
          <OptionsDataFilter
            column={column}
            handleFilter={handleFilter}
            options={[
              { label: '(10000) Generic', value: '10000' },
              { label: '(10001) Authentication', value: '10001' },
              { label: '(10002) Rest Call', value: '10002' },
            ]}
          />
        );
      }
    ) : null,
    new TextDataColumn('message', 'Message', false, true, isMobile ? 75 : 35,
      (value) => (
        <div className={css.textCell}>{value}</div>
      )
    ),
  ].filter((col): col is DataColumn<DataTableRow> => col !== null);

  const processLogs = (): LogViewerRow[] => {
    let rows: LogViewerRow[] = [];
    
    if (dataState.data !== undefined && Array.isArray(dataState.data.list)) {
      try {
        rows = dataState.data.list.map((item) => {
          const dateTime = item.dateTime ? new Date(item.dateTime) : new Date();
          
          // Validate date
          if (isNaN(dateTime.getTime())) {
            console.warn('Invalid date encountered:', item.dateTime);
          }
          
          return {
            id: item._id || `id-${Math.random()}`,
            requestId: item.requestId || '',
            dateTime: dateTime,
            logLevel: item.logLevel || 'Information',
            user: item.user || '',
            className: item.className || '',
            operation: item.operation || '',
            eventId: item.eventId ? `(${item.eventId}) ${item.eventName || ''}` : '',
            message: item.message || '',
          };
        });
      } catch (err) {
        console.error('Error processing logs:', err);
        setError('Error processing log data');
      }
    }
    
    return rows;
  };

  const rows = processLogs();
  const count = dataState.data ? dataState.data.count || 0 : 0;

  const handleSort = (sc: SortColumn | undefined) => {
    runSort(sc);
  };

  const handleFilter = (filters: FilterColumn[]) => {
    runFilters(filters);
  };

  const handleLoadMore = () => {
    loadPage();
  };

  return (
    <div className={css.logViewerWrapper}>
      <div className={css.logViewerContainer}>
        {error || dataState.error ? (
          <div className={css.errorMessage}>
            {error || (dataState.error && dataState.error.message) || 'An error occurred while loading logs'}
          </div>
        ) : (
          <AdminDataTable
            columns={columns}
            rows={rows}
            total={count}
            allowFilter={true}
            allowNoSort={false}
            onSort={handleSort}
            onFilter={handleFilter}
            onLoadMore={handleLoadMore}
            state={dataState}
          />
        )}
      </div>
    </div>
  );
};

export default LogViewer;